import React, { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import ErrorFilledIcon from '@monofront/design-system/visuals/Icons/ErrorFilled';
import {
  Input,
  ErrorText,
  TextInput,
  Label,
} from '@monofront/design-system/components/FormV2';
import { colors, fontWeight } from '@monofront/theme';
import { useTranslations } from 'next-intl';
import { Frames, CardNumber, ExpiryDate, Cvv } from '@monofront/frames-react';
import getConfig from 'next/config';
import isProductionMode from '@utils/isProductionMode';
import { toast } from 'react-toastify';
import logger from '@utils/logger';
import FormContext from '@components/PaymentForms/FormContext';
import Popover from '@components/Popover';
import { IconSizes } from '@monofront/design-system/visuals/Icons/type';
import references from '@monofront/design-system/css-library/tokens/reference';
import { useCheckoutComMachine } from '@monofront/business/contexts';
import { CHECKOUT_COM_CARD_FORM_STATES } from '@monofront/business/stateMachines';
import HeartLoader from '@monofront/design-system/components/HeartLoader';

interface ICheckoutComCardFormAbTestProps {
  isLoading?: boolean;
  onComplete?: (isValid: boolean) => void;
  children: React.ReactNode;
  onSubmit?: (formData: any) => void;
}

const { publicRuntimeConfig } = getConfig();

const CheckoutComCardFormAbTest: React.FC<ICheckoutComCardFormAbTestProps> = ({
  children,
  isLoading,
  onComplete,
  onSubmit,
}) => {
  const {
    register,
    handleSubmit,
    setError,
    clearErrors,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: 'onChange',
    criteriaMode: 'all',
  });
  const [isCardFormReady, setIsCardFormReady] = useState(false);
  const [isCardFormCompleted, setIsCardFormCompleted] = useState(false);
  const { interpreter } = useCheckoutComMachine();

  const holderName = watch('holderName', '');
  const tAccount = useTranslations('Account');
  const tGlobal = useTranslations('Global');
  const tCard = useTranslations('Card');

  const errorCards = {
    'expiry-date': tCard('invalidExpiryDate'),
    cvv: tCard('incompleteCVC'),
    'card-number': tCard('invalidCardNumber'),
  };

  useEffect(() => {
    if (
      isCardFormCompleted &&
      'holderName' in dirtyFields &&
      !errors.holderName
    ) {
      onComplete?.(true);

      return;
    }

    onComplete?.(false);
  }, [dirtyFields, holderName, errors, isCardFormCompleted]);

  // enable re-submit form after a reset
  useEffect(() => {
    const handler = async (state) => {
      if (state.value === CHECKOUT_COM_CARD_FORM_STATES.INITIAL) {
        Frames.enableSubmitForm();
      }
    };

    interpreter?.onTransition(handler);

    return () => {
      interpreter?.off(handler);
    };
  }, [interpreter]);

  return (
    <FormContext.Provider
      value={useMemo(
        () => ({ clearErrors, errors, isLoading, register, setError }),
        [clearErrors, errors, isLoading, register, setError],
      )}
    >
      <Frames
        ready={() => {
          setIsCardFormReady(true);
        }}
        config={{
          debug: !isProductionMode,
          publicKey: publicRuntimeConfig.checkoutComPublicKey,
          localization: {
            cardNumberPlaceholder: tCard('cardNumber'),
            expiryMonthPlaceholder: tCard('expiryMonthPlaceholder'),
            expiryYearPlaceholder: tCard('expiryYearPlaceholder'),
            cvvPlaceholder: tCard('cardCVCPlaceholderAbTest'),
          },
          cardholder: {
            name: holderName,
          },
          style: {
            base: {
              fontFamily: 'Arial',
              fontSize: '16px',
              fontWeight: fontWeight.light,
              color: colors.black,
              letterSpacing: 'inherit',
            },
            invalid: {
              color: colors.danger,
            },
            placeholder: {
              base: {
                fontFamily: 'Arial',
                color: colors.grey1,
                borderRadius: '8px',
              },
            },
          },
        }}
        cardValidationChanged={(e) => setIsCardFormCompleted(e.isValid)}
        frameValidationChanged={(e) => {
          if (!e.isValid) {
            setError(e.element, {
              type: 'cardError',
              message: errorCards[e.element],
            });
          } else {
            clearErrors(e.element);
          }
        }}
        cardTokenizationFailed={(e) => {
          logger.error('Card tokenization failed', {
            error: e,
          });
          toast(tGlobal('errorOccurredTryAgain'), {
            progressClassName: 'toast-error',
          });
        }}
      >
        {!isCardFormReady && <HeartLoader />}
        <div
          className={`tw-flex tw-w-full tw-justify-center ${
            isCardFormReady ? '' : 'tw-hidden'
          }`}
        >
          <form onSubmit={onSubmit && handleSubmit(onSubmit)}>
            <TextInput id="card-owner" isInvalid={!!errors.holderName?.message}>
              <Label>{tCard('cardOwnerAbTest')}</Label>
              <Input
                placeholder={tCard('cardOwnerPlaceholderAbTest')}
                {...register('holderName', {
                  required: {
                    value: true,
                    message: tAccount('mandatoryField'),
                  },
                })}
                dataTestId="CardForm-Input-2"
              />
              <ErrorText>{errors.holderName?.message as string}</ErrorText>
            </TextInput>

            <div className="card tw-my-2">
              <div
                className={`card-item ${
                  errors['card-number'] ? 'card-item-error' : ''
                }`}
              >
                <p
                  className="tw-lf-body-regular-md tw-mb-1 tw-mt-4"
                  id="checkout-frames-card-number"
                >
                  {tCard('cardNumber')}
                </p>
                <CardNumber className="!tw-rounded-lg" />
                {errors['card-number'] && (
                  <span className={`tw-lf-form-input-bottom-message tw-flex`}>
                    <span>
                      <ErrorFilledIcon
                        color={references.colors.red[600]}
                        size={IconSizes.sm}
                      />
                    </span>
                    <span className="tw-lf-input-error-message">
                      {errors['card-number']?.message as string}
                    </span>
                  </span>
                )}
              </div>
              <div className="card-line">
                <div
                  className={`card-item ${
                    errors['expiry-date'] ? 'card-item-error' : ''
                  }`}
                >
                  <p
                    className="tw-lf-body-regular-md tw-mb-1 tw-mt-4"
                    id="checkout-frames-expiry-date"
                  >
                    {tCard('expirationDate')}
                  </p>
                  <ExpiryDate className="!tw-rounded-lg" />
                  {errors['expiry-date'] && (
                    <span className={`tw-lf-form-input-bottom-message tw-flex`}>
                      <span>
                        <ErrorFilledIcon
                          color={references.colors.red[600]}
                          size={IconSizes.sm}
                        />
                      </span>
                      <span className="tw-lf-input-error-message">
                        {errors['expiry-date']?.message as string}
                      </span>
                    </span>
                  )}
                </div>
                <div
                  className={`card-item ${
                    errors['cvv'] ? 'card-item-error' : ''
                  }`}
                >
                  <div className="label-with-popover tw-mt-4 tw-flex tw-flex-row tw-align-baseline">
                    <p
                      className="tw-lf-body-regular-md tw-mb-1 tw-mr-1 tw-inline-block"
                      id="checkout-frames-cvv"
                    >
                      {tCard('cardCVCAbTest')}
                    </p>
                    <Popover content={<span>{tCard('CVCPopOver')}</span>}>
                      <span
                        className="tw-lf-caption-strong tw-mb-1 tw-inline-flex tw-h-4 tw-w-4 tw-items-center tw-justify-center tw-rounded-full tw-bg-black-1000 tw-text-white"
                        aria-label={tGlobal('moreInformation')}
                      >
                        {'?'}
                      </span>
                    </Popover>
                  </div>
                  <Cvv className="!tw-rounded-lg" />
                  {errors['cvv'] && (
                    <span className={`tw-lf-form-input-bottom-message tw-flex`}>
                      <span>
                        <ErrorFilledIcon
                          color={references.colors.red[600]}
                          size={IconSizes.sm}
                        />
                      </span>
                      <span className="tw-lf-input-error-message">
                        {errors['cvv']?.message as string}
                      </span>
                    </span>
                  )}
                </div>
              </div>
            </div>
            {children}
          </form>
        </div>
        <style jsx>{`
          .error {
            color: var(--clr-danger);
            padding: var(--space-1) var(--space-2) 0;
          }

          .card-item {
            width: 100%;
            margin-bottom: var(--space-2);
          }

          .card-item-error > :global(div) {
            border-color: var(--clr-danger) !important;
          }

          .card-item > :global(div) {
            border: 1px solid var(--clr-tertiary-2);
            border-radius: var(--rd-6);
            padding: 0 var(--space-3);
            height: 42px;
          }

          .card-line {
            display: flex;
          }

          .card-line > .card-item:nth-child(even) {
            margin-left: var(--space-4);
          }

          div.label-with-popover {
            border: none;
            padding: 0;
            height: initial;
          }

          .label-with-popover button {
            margin-left: var(--space-1);
            display: inline;
          }

          .label-with-popover :global(label) {
            display: inline;
          }
        `}</style>
      </Frames>
    </FormContext.Provider>
  );
};

export default CheckoutComCardFormAbTest;
